type OrganizationSelection = {
  currentOrganization: {
    id?: string
    slug?: string
    isExternalAccess?: boolean
  } | null
  externalOrgRelationship: {
    id?: string
    hostOrganizationId?: string
    status?: string
    revokedAt?: unknown
  } | null
}

export class OrganizationContextError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'OrganizationContextError'
  }
}

/** Explicit cross-workspace targets never inherit the selected workspace's partner or slug. */
export function resolveOrganizationContext(
  selection: OrganizationSelection,
  headers: Record<string, string>,
  includeSelectedOrganization: boolean,
) {
  const supplied = new Headers(headers)
  for (const key of ['x-organization-id', 'x-external-org-relationship-id']) {
    const value = supplied.get(key)
    if (value !== null && (!value || value.includes(','))) {
      throw new OrganizationContextError(
        'Workspace access is ambiguous. Select the workspace again.',
      )
    }
  }
  const explicitOrganization = supplied.get('x-organization-id')
  const explicitRelationship = supplied.get('x-external-org-relationship-id')
  const current = selection.currentOrganization
  const orgId =
    explicitOrganization ??
    (includeSelectedOrganization ? (current?.id ?? null) : null)
  if (!orgId && explicitRelationship) {
    throw new OrganizationContextError('Partner access requires a workspace.')
  }
  const isSelected = !!orgId && orgId === current?.id
  let externalRelationshipId = explicitRelationship
  let slug = supplied.get('x-organization-slug')
  if (isSelected) {
    const relationship = current?.isExternalAccess
      ? selection.externalOrgRelationship
      : null
    if (
      current?.isExternalAccess &&
      (!relationship?.id ||
        relationship.hostOrganizationId !== orgId ||
        relationship.status !== 'ACTIVE' ||
        relationship.revokedAt)
    ) {
      throw new OrganizationContextError(
        'Partner access is unavailable. Select the workspace again.',
      )
    }
    if (
      explicitRelationship !== null &&
      explicitRelationship !== relationship?.id
    ) {
      throw new OrganizationContextError(
        'The selected workspace access changed. Select the workspace again.',
      )
    }
    if (slug !== null && current?.slug && slug !== current.slug) {
      throw new OrganizationContextError(
        'The workspace address changed. Select the workspace again.',
      )
    }
    externalRelationshipId = relationship?.id ?? null
    slug = current?.slug ?? slug
  }
  return { orgId, slug, externalRelationshipId }
}
